// Appels HTTP typés vers /api/invoice-uploads/* — flow upload + OCR + revue.
//
// Flow type d'utilisation côté page :
//   1. init({ filename, content_type, size_bytes })        → { upload_url, headers, upload }
//   2. putToS3(upload_url, headers, file)                  → fetch direct S3 (pas via api.*)
//   3. confirm(upload.id)                                  → upload.status = uploaded
//   4. (OCR auto par le worker arq) … attendre ocr_done   → poll list() ou get(id)
//   5. reviewContext(id)                                   → charger la page de revue
//   6. validate(id, payload)                               → crée Invoice, upload devient discarded

import { api } from "./client";
import type {
  InvoiceFromExtractionPayload,
  InvoicePublic,
  InvoiceUploadPublic,
  OcrExtractionPublic,
  ReviewContext,
  UploadInitRequest,
  UploadInitResponse,
  UploadStatus,
  UUID,
} from "./types";

export type ListUploadsParams = {
  status?: UploadStatus;
  limit?: number;
  offset?: number;
};

function buildQuery(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    parts.push(
      `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    );
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

/**
 * PUT direct vers S3 (MinIO en dev) via l'URL presigned.
 *
 * On utilise `globalThis.fetch` — PAS le wrapper `api.*` — parce que :
 *  - le presigned URL pointe vers S3, pas vers notre API, donc pas besoin du
 *    header Authorization (il serait même refusé par la signature)
 *  - on veut pouvoir suivre la progression en réseau sans passer par notre
 *    refresh-token logic
 */
export async function putToS3(
  url: string,
  headers: Record<string, string>,
  file: File,
): Promise<void> {
  const resp = await globalThis.fetch(url, {
    method: "PUT",
    headers,
    body: file,
  });
  if (!resp.ok) {
    throw new Error(
      `Upload S3 échoué (HTTP ${resp.status}) — vérifie la configuration CORS`,
    );
  }
}

export const invoiceUploadsApi = {
  list: (params: ListUploadsParams = {}) =>
    api.get<InvoiceUploadPublic[]>(
      `/api/invoice-uploads${buildQuery(params)}`,
    ),

  get: (id: UUID) => api.get<InvoiceUploadPublic>(`/api/invoice-uploads/${id}`),

  init: (payload: UploadInitRequest) =>
    api.post<UploadInitResponse>("/api/invoice-uploads", payload),

  confirm: (id: UUID) =>
    api.post<InvoiceUploadPublic>(`/api/invoice-uploads/${id}/confirm`),
  directUpload: (file: File, invoiceType: "customer" | "supplier" = "customer") =>
    directUpload(file, invoiceType),

  delete: (id: UUID) => api.delete<void>(`/api/invoice-uploads/${id}`),

  getExtraction: (id: UUID) =>
    api.get<OcrExtractionPublic>(`/api/invoice-uploads/${id}/extraction`),

  reviewContext: (id: UUID) =>
    api.get<ReviewContext>(`/api/invoice-uploads/${id}/review-context`),

  reOcr: (id: UUID) =>
    api.post<InvoiceUploadPublic>(`/api/invoice-uploads/${id}/ocr`),

  validate: (id: UUID, payload: InvoiceFromExtractionPayload) =>
    api.post<InvoicePublic>(`/api/invoice-uploads/${id}/validate`, payload),

  downloadUrl: (id: UUID) =>
    api.get<{ url: string; expires_in: number }>(
      `/api/invoice-uploads/${id}/download-url`,
    ),
};

/** Upload direct via le backend (pas de presigned URL). */
export async function directUpload(file: File, invoiceType: "customer" | "supplier" = "customer"): Promise<InvoiceUploadPublic> {
  const { getAccessTokenForUpload } = await import("./client");
  const token = getAccessTokenForUpload();
  if (!token) throw new Error("Non authentifié");
  const formData = new FormData();
  formData.append("file", file);
  formData.append("invoice_type", invoiceType);
  const resp = await globalThis.fetch("/api/invoice-uploads/direct", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }));
    throw new Error(body.detail || `Upload échoué (${resp.status})`);
  }
  return resp.json();
}
